import { ChevronDownIcon } from 'lucide-react';
import { useCan } from '../useCan';
import { useCosts } from '../margin/useCosts';
import { MarginLines } from '../margin/MarginLines';
import { orderContribution, orderKindLabel, orderKindOf } from '../../core/rules/margin';
import { refundVatByRate } from '../../core/rules/revenue';
import type { AdminOrder } from '../../core/types/adminRequests';

/**
 * Bestelling › Wat er overblijft. Ingeklapt; alleen met `finance.margin`. Geschat: inkoop is de
 * huidige gemiddelde prijs (de server legt hem bij verzenden per regel vast) en de kosten komen uit
 * Instellingen › Kosten en marge tot de echte Mollie- en PostNL-cijfers er zijn.
 */
export function OrderMarginPanel({
  order: o
}: {
  order: AdminOrder;
}) {
  const {
    canAll
  } = useCan();
  const allowed = canAll('finance.margin');
  const {
    data
  } = useCosts(allowed);
  if (!allowed) return null;
  const kind = o.kind ?? orderKindOf(o.channel, o.lines);
  const counts = o.paymentStatus === 'betaald' && !o.cancelledAt;
  return <details className="group rounded-lg border border-line bg-white" data-id="element-3574" data-mp="rxf168yqdmlb">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-4 sm:px-5 [&::-webkit-details-marker]:hidden" data-id="element-3575" data-mp="mz3pdsovl20g">
        <span className="min-w-0" data-id="element-3576" data-mp="11motnpxhl79y">
          <span className="block text-[15px] font-medium text-ink" data-id="element-3577" data-mp="11q2z571g5wlm">Wat er overblijft</span>
          <span className="block text-[13px] text-muted" data-id="element-3578" data-mp="wr02oazj8yp9">
            {orderKindLabel[kind]} · geschat · vóór vaste lasten
          </span>
        </span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted transition-transform duration-200 ease-out group-open:rotate-180" strokeWidth={1.75} aria-hidden="true" data-id="element-3579" data-mp="411ai6q1klgd" />
      </summary>
      <div className="border-t border-line px-4 py-4 sm:px-5" data-id="element-3580" data-mp="dxw6ahuxk7qy">
        {!counts ? <p className="text-[13px] text-muted" data-id="element-3581" data-mp="1ucf9c3eroh86">Telt niet mee: deze bestelling is niet betaald of geannuleerd.</p> : !data ? <p className="text-[13px] text-muted" data-id="element-3582" data-mp="1pcj7a9pmqzv9">Laden…</p> : <>
            <MarginLines breakdown={orderContribution({
          kind,
          lines: o.lines.map(l => ({
            name: l.name,
            qty: l.qty,
            grossCents: l.lineTotalCents,
            vatRate: l.vatRate,
            costCents: data.itemCosts[l.productId] ?? null
          })),
          paymentMethod: kind === 'bijvullen' ? 'incasso' : o.paymentMethod,
          paidCents: Math.max(0, o.totalCents - o.voucherCents),
          shipping: o.address.country === 'BE' ? 'pakket-be' : 'pakket-nl',
          shippingChargedCents: o.shippingCents,
          shippingVatRate: o.shippingVatRate,
          // Per tarief, met dezelfde verdeling als de creditnota (niet alles is 21%).
          refundedNetCents: o.refunds.reduce((n, r) => n + refundVatByRate(o, r).reduce((s, p) => s + p.grossCents - p.vatCents, 0), 0),
          // Ongeopend terug op voorraad: die inkoop is er weer.
          returnedCostCents: o.refunds.filter(r => r.restocked).flatMap(r => r.lines ?? []).reduce((n, l) => n + l.qty * (data.itemCosts[l.productId] ?? 0), 0)
        }, data.costs)} className="max-w-md" data-id="element-3583" data-mp="1whk7w50oeu40" />
            {o.gift && <p className="mt-3 text-[12px] text-muted" data-id="element-3584" data-mp="2z8bqofp8io7s">Het cadeau ({o.gift}) telt via de voorraad: het wordt afgeschreven tegen de inkoopprijs.</p>}
          </>}
      </div>
    </details>;
}